// ./src/components/features/LostFound/LostFoundFilterBar/LostFoundFilterBar.tsx
//
// ROADMAP #68 — filters for lost & found: where a record stands (the tab's own states — an item's or a report's) and what
// it is. Fully controlled — the page maps the value to the list filter (both server-side). One bar for both tabs; the
// page hands it that tab's states.
import React from "react";
import { Group, Button, Box, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, SelectField } from "@/components/generic";

import { lostFoundCategoryOptions } from "../lostFoundLabels";

export interface LostFoundFilterValue {
  state: string | null;
  category: string | null;
}

export interface LostFoundFilterBarProps {
  value: LostFoundFilterValue;
  onChange: (value: LostFoundFilterValue) => void;
  /** The tab's states (an item's or a report's). */
  stateOptions: { value: string; label: string }[];
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_LOST_FOUND_FILTER: LostFoundFilterValue = { state: null, category: null };

export const countLostFoundFilters = (value: LostFoundFilterValue): number => [value.state, value.category].filter(Boolean).length;

const FIELD_STYLE: React.CSSProperties = { flex: "1 1 200px", minWidth: 190 };

const LostFoundFilterBarComponent: React.FC<LostFoundFilterBarProps> = ({ value, onChange, stateOptions, disabled = false, showClear = true, vertical = false }) => {
  const { t } = useTranslation(["lostFound", "common"]);

  const stateControl = (
    <Box style={vertical ? undefined : FIELD_STYLE}>
      <SelectField
        label={t("lostFound:filters.state", { defaultValue: "Status" })}
        placeholder={t("lostFound:filters.any", { defaultValue: "Any" })}
        data={stateOptions}
        value={value.state}
        onChange={(_n, v) => onChange({ ...value, state: v ?? null })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  const categoryControl = (
    <Box style={vertical ? undefined : FIELD_STYLE}>
      <SelectField
        label={t("lostFound:filters.category", { defaultValue: "What it is" })}
        placeholder={t("lostFound:filters.any", { defaultValue: "Any" })}
        data={lostFoundCategoryOptions(t)}
        value={value.category}
        onChange={(_n, v) => onChange({ ...value, category: v ?? null })}
        clearable
        searchable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  if (vertical) {
    return (
      <Stack gap="md">
        {stateControl}
        {categoryControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {stateControl}
      {categoryControl}

      {countLostFoundFilters(value) > 0 && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_LOST_FOUND_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const LostFoundFilterBar = React.memo(LostFoundFilterBarComponent);
